import React, { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { useSirenEntity, sirenActions, entityStore } from '~/utility/BinSentry-ui-utility';
import {
  makeStyles,
  Button,
  DialogActions,
  Paper,
  FormControl,
  FormGroup,
  TextField,
  DialogContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Icons,
} from '~/lib/bn-material-ui';
import { BnDialog } from '~/common/bn-dialog';
import { BinPathName } from '~/bins/bin-path-name.tsx';
import { BnLoadingSkeleton } from '~/common/bn-loading-skeleton';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => {
  return {
    errorContainer: {
      padding: '1rem',
      backgroundColor: theme.palette.red[30],
      color: 'white',
    },
    binName: {
      fontWeight: 'bold',
    },
    untilNoOrdersInfo: {
      padding: '1rem',
      margin: '1rem 0',
    },
    dialogActions: {
      margin: '1rem 1.5rem',
      padding: 0,
    },
    untilDateFieldSet: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    untilDateTextField: {
      width: '4.5rem',
      '& input': {
        padding: '.75rem',
      },
    },
    daysLabel: {
      marginLeft: '1rem',
    },
    saveContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1.5em',
    },
    savedTextPositionRight: {
      flexDirection: 'row-reverse',
      justifyContent: 'flex-end',
    },
    recentlySaved: {
      color: theme.palette.blue[40],
      display: 'flex',
      alignItems: 'center',
      gap: '0.5em',
    },
  };
});

export default function OrderSuggestionSnoozeDialog({ binHref, open, onClose, onSnoozeUpdated, showUnSnoozedOption }) {
  const styles = useStyles();
  const formData = useSuggestionSnoozeFormData({
    binHref: open ? binHref : undefined,
    onSnoozeUpdated,
    showUnSnoozedOption,
  });

  return (
    <BnDialog
      maxWidth='md'
      fullWidth={true}
      open={open}
      onClose={onClose}
      overrideDialogActions={true}
      title={<TitleText styles={styles} binHref={binHref} />}
    >
      <DialogContent>
        <SuggestionSnoozeForm {...formData} />
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <SuggestionSnoozeSaveButton {...formData} />
      </DialogActions>
    </BnDialog>
  );
}

function TitleText({ binHref, styles }) {
  const { entity: binEntity } = useSirenEntity({ href: binHref });

  return (
    <div>
      <Typography variant='subtitle2'>
        <BinPathName binHref={binHref} />
      </Typography>
      <Typography variant='h5' className={styles.binName}>
        {binEntity?.properties?.name}
      </Typography>
    </div>
  );
}

export function useSuggestionSnoozeFormData({ binHref, onSnoozeUpdated, showUnSnoozedOption }) {
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedSnoozeType, setSelectedSnoozeType] = useState('');
  const [snoozeLengthDays, setSnoozeLengthDays] = useState(1);

  const { entity: binEntity, loading: loadingBin } = useSirenEntity({ href: binHref });
  const { entity: ordersEntity, loading: loadingOrders } = useSirenEntity({ href: binEntity?.getSubEntityByRel('https://api.binsentry.com/rel/v2/orders')?.href });
  const settingsHref = ordersEntity?.getLinkByRel('https://api.binsentry.com/rel/order-suggestion-settings')?.href;
  const { entity: settingsEntity, loading: loadingSettings } = useSirenEntity({ href: settingsHref });
  const snoozeHref = settingsEntity?.getSubEntityByRel('https://api.binsentry.com/rel/order-suggestion-snooze')?.getLinkByRel('self').href;
  const { entity: snoozeEntity, loading: loadingSnooze } = useSirenEntity({ href: snoozeHref });

  const loading = loadingBin || loadingOrders || loadingSettings || loadingSnooze;
  const snoozeType = snoozeEntity?.properties?.snoozeType || '';
  const isSnoozedUntilNoOrders = snoozeType === 'until-no-orders';
  const isSnoozedUntilDate = snoozeType === 'until-date';
  const deleteOnlyAction = isSnoozedUntilNoOrders || isSnoozedUntilDate;
  useEffect(() => setSelectedSnoozeType(snoozeType), [snoozeType]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    const clearingSnooze = deleteOnlyAction || selectedSnoozeType === '';
    let success = true;

    try {
      let action;
      let fields;
      if (clearingSnooze) {
        action = snoozeEntity.getAction('clear-snooze');
        fields = sirenActions.getActionFields(action);
      } else {
        action = snoozeEntity.getAction(snoozeType ? 'update-snooze' : 'snooze-suggestions');
        fields = sirenActions.getActionFields(action);
        fields.set('snoozeType', selectedSnoozeType);
        if (selectedSnoozeType === 'until-date') {
          const untilISO = DateTime.now().plus({ days: snoozeLengthDays }).setZone('utc').toISO();
          fields.set('snoozedUntil', untilISO);
        } else {
          fields.delete('snoozedUntil');
        }
      }

      await sirenActions.submitAction(action, fields);
      await Promise.all([
        entityStore.invalidateHref(settingsHref),
        entityStore.invalidateHref(snoozeHref),
      ]);
      onSnoozeUpdated && onSnoozeUpdated(selectedSnoozeType);
    } catch (e) {
      console.error(e);
      setError(e.message || 'An unexpected error has occurred.');
      success = false;
    }

    setSubmitting(false);
    return success;
  };

  return {
    error,
    deleteOnlyAction,
    isSnoozedUntilNoOrders,
    isSnoozedUntilDate,
    snoozeEntity,
    selectedSnoozeType,
    setSelectedSnoozeType,
    showUnSnoozedOption,
    submitting,
    loading,
    setSnoozeLengthDays,
    snoozeLengthDays,
    onSnoozeUpdated,
    submit,
  };
}

export function SuggestionSnoozeSaveButton({
  submitting,
  snoozeEntity,
  selectedSnoozeType,
  deleteOnlyAction,
  submit,
  savedTextPositionRight = false,
  loading,
}) {
  const styles = useStyles();
  const [recentlySaved, setRecentlySaved] = useState(false);
  const unsetRecentlySavedRef = useRef();
  const stillNotSnoozed = !selectedSnoozeType && snoozeEntity?.properties.isSnoozed === false;

  return (
    <BnLoadingSkeleton loading={loading}>
      <div className={
        classNames(styles.saveContainer, savedTextPositionRight && styles.savedTextPositionRight)
      }>
        {recentlySaved ?
          (<span className={styles.recentlySaved}><Icons.Check />Saved</span>) :
          null
        }
        <Button
          variant='contained'
          disabled={submitting || !snoozeEntity?.properties || stillNotSnoozed}
          onClick={async () => {
            const wasSuccess = await submit();
            if (!wasSuccess) {
              return;
            }

            if (unsetRecentlySavedRef.current) {
              clearTimeout(unsetRecentlySavedRef.current);
            }
            unsetRecentlySavedRef.current = setTimeout(() => setRecentlySaved(false), 1500);
            setRecentlySaved(true);
          }}
        >
          {deleteOnlyAction ? 'Un Snooze' : 'Save'}
        </Button>
      </div>
    </BnLoadingSkeleton>
  );
}

export function SuggestionSnoozeForm({
  error,
  submitting,
  snoozeEntity,
  deleteOnlyAction,
  isSnoozedUntilNoOrders,
  isSnoozedUntilDate,
  selectedSnoozeType,
  setSelectedSnoozeType,
  showUnSnoozedOption,
  setSnoozeLengthDays,
  snoozeLengthDays,
  loading,
}) {
  const styles = useStyles();

  return (
    <BnLoadingSkeleton loading={loading}>
      <div>
        {error &&
        <Paper className={styles.errorContainer}>
          <Typography variant='h6'>Error</Typography>
          {error}
        </Paper>
        }
        {deleteOnlyAction && (
          <Paper className={styles.untilNoOrdersInfo} variant='outlined'>
            {isSnoozedUntilNoOrders && 'Order suggestions are snoozed until no active orders are present for this bin.'}
            {isSnoozedUntilDate && `Order suggestions are snoozed until: ${DateTime.fromISO(snoozeEntity?.properties?.snoozedUntil).toLocaleString()}`}
          </Paper>
        )}
        {!deleteOnlyAction && (
          <FormGroup>
            <FormControl margin='normal'>
              {selectedSnoozeType !== null && (
                <RadioGroup
                  component='fieldset'
                  aria-label='snooze type'
                  value={selectedSnoozeType}
                  name='radio-buttons-group'
                  onChange={e => {
                    setSelectedSnoozeType(e.target.value);
                  }}
                >
                  {showUnSnoozedOption &&
                    <FormControlLabel control={<Radio disabled={submitting} value='' />} label='Suggest orders for this bin' />
                  }
                  <div className={styles.untilDateFieldSet}>
                    <FormControlLabel control={<Radio disabled={submitting} value='until-date' />} label='Pause suggestions for' />
                    <TextField
                      variant='outlined'
                      type='number'
                      className={styles.untilDateTextField}
                      disabled={submitting || selectedSnoozeType !== 'until-date'}
                      onChange={(event) => setSnoozeLengthDays(event.target.value)}
                      value={snoozeLengthDays}
                      InputProps={{
                        inputProps: { step: '1', min: '0', max: '99' },
                      }}
                    />
                    <Typography variant='body1' className={styles.daysLabel}>days</Typography>
                  </div>
                  <FormControlLabel control={<Radio disabled={submitting} value='until-next-refill' />} label='Pause suggestions until the next refill' />
                  <FormControlLabel control={<Radio disabled={submitting} value='forever' />} label='Do not suggest orders for this bin' />
                </RadioGroup>
              )}
            </FormControl>
          </FormGroup>
        )}
      </div>
    </BnLoadingSkeleton>
  );
}
